import React from 'react';
import { motion } from 'motion/react';
import { BDAY_EASE } from '../theme';
import type { BirthdayContent, BirthdaySnapshot } from '../content';
import { SectionBgImage, hasSectionBg } from './SectionBgImage';

const PLACEHOLDER: BirthdaySnapshot[] = [
  { imageUrl: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=400&q=80', caption: 'First Smile' },
  { imageUrl: 'https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?w=400&q=80', caption: 'First Steps' },
  { imageUrl: 'https://images.unsplash.com/photo-1513151233558-d860c5398176?w=400&q=80', caption: 'First Birthday' },
];

const EMOJI_BADGES = ['🎂', '✨', '🎉', '🥳', '🎈', '💫', '🌟', '🎁', '🎊', '💙', '🎀', '🦋'];

function SnapshotCard({ snap, index }: { snap: BirthdaySnapshot; index: number }) {
  const offsets = ['translateY(0px)', 'translateY(-24px)', 'translateY(12px)', 'translateY(-16px)'];
  const rotations = ['-rotate-2', 'rotate-1', '-rotate-1', 'rotate-2'];
  const offset = offsets[index % offsets.length];
  const rotation = rotations[index % rotations.length];
  const badge = EMOJI_BADGES[index % EMOJI_BADGES.length];

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.12, ease: BDAY_EASE }}
      whileHover={{ scale: 1.05, rotate: 0, y: -8 }}
      style={{ transform: offset }}
      className={`relative flex-shrink-0 ${rotation} group`}
    >
      {/* glow ring */}
      <div className="absolute -inset-2 rounded-2xl bg-blue-500/15 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      <div className="relative w-44 h-56 sm:w-52 sm:h-64 rounded-2xl overflow-hidden border-2 border-blue-400/25 shadow-xl shadow-blue-900/50">
        <img
          src={snap.imageUrl}
          alt={snap.caption ?? `Snapshot ${index + 1}`}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        {/* bottom caption overlay */}
        {snap.caption && (
          <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent pt-6 pb-3 px-3">
            <p className="text-white text-xs font-medium leading-tight text-center">{snap.caption}</p>
          </div>
        )}
        {/* top-right shimmer */}
        <div className="absolute inset-0 bg-gradient-to-t from-blue-900/30 via-transparent to-transparent" />
      </div>

      {/* floating badge */}
      <motion.div
        animate={{ y: [0, -5, 0] }}
        transition={{ duration: 2.5 + (index % 3) * 0.4, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute -top-3 -right-3 bg-blue-500 rounded-full w-9 h-9 flex items-center justify-center text-lg shadow-lg"
      >
        {badge}
      </motion.div>
    </motion.div>
  );
}

export function SnapshotGallerySection({ content }: { content: BirthdayContent }) {
  const snaps = content.snapshots.length >= 1 ? content.snapshots : PLACEHOLDER;
  const capped = snaps.slice(0, 12);

  if (capped.length === 0) return null;

  // Split into rows of 3-4 for a masonry feel
  const row1 = capped.slice(0, Math.min(4, capped.length));
  const row2 = capped.slice(4, 8);
  const row3 = capped.slice(8, 12);

  const bg = content.sectionBackgrounds.gallery;
  const active = hasSectionBg(bg);

  return (
    <section
      className="relative py-20 px-6 overflow-hidden"
      style={active ? undefined : { background: 'linear-gradient(180deg, #1e3a5f 0%, #0f172a 100%)' }}
    >
      <SectionBgImage bg={bg} />
      {/* section heading */}
      <div className="text-center mb-14">
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: BDAY_EASE }}
          className="text-blue-400 text-xs font-bold uppercase tracking-[0.25em] mb-2"
        >
          📷 Memories
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1, ease: BDAY_EASE }}
          className="text-3xl sm:text-4xl font-bold text-white"
        >
          Birthday Snapshots
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-3 text-blue-300 text-sm"
        >
          Cherished moments, forever framed
        </motion.p>
      </div>

      {/* Gallery rows */}
      <div className="max-w-5xl mx-auto space-y-8">
        {[row1, row2, row3].filter((r) => r.length > 0).map((row, rowIdx) => (
          <div key={rowIdx} className="flex flex-wrap items-center justify-center gap-5 sm:gap-8">
            {row.map((snap, colIdx) => (
              <SnapshotCard key={`${rowIdx}-${colIdx}`} snap={snap} index={rowIdx * 4 + colIdx} />
            ))}
          </div>
        ))}
      </div>

      {/* decorative line */}
      <div className="mt-16 mx-auto w-24 h-px bg-gradient-to-r from-transparent via-blue-400 to-transparent" />
    </section>
  );
}
